import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { requestOtp, verifyOtp, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BRAND, WEBSITE } from "@/lib/brand";
import { BrandMark } from "@/components/BrandMark";

export default function Login() {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const otpRef = useRef(null);

  const cleanPhone = phone.replace(/\D/g, "").slice(-10);

  const sendOtp = async () => {
    if (cleanPhone.length !== 10) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await requestOtp(cleanPhone);
      setStep("otp");
      toast.success(data?.message || "OTP sent to your phone");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (c) => {
    const otp = (c ?? code).trim();
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await verifyOtp(cleanPhone, otp);
      if (!data?.token) throw new Error("Login failed");
      login(data.token, data.user);
      toast.success("Welcome back" + (data?.user?.name ? ", " + data.user.name : ""));
      navigate("/orders", { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 sm:px-14 lg:px-20 py-12">
        <div className="max-w-sm w-full mx-auto lg:mx-0">
          <BrandMark className="mb-14" textClass="text-2xl" iconClass="h-14 w-14" />

          {step === "phone" ? (
            <motion.div
              key="phone"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-3">
                <Store className="h-3.5 w-3.5" /> Vendor Portal
              </p>
              <h1 className="font-display font-black text-4xl sm:text-5xl leading-tight mb-3">
                Run your kitchen.
              </h1>
              <p className="text-muted-foreground mb-10 leading-relaxed">
                Sign in with your registered shop phone number to manage live orders.
              </p>

              <label className="text-xs uppercase tracking-[0.12em] text-muted-foreground font-medium">
                Phone number
              </label>
              <div className="flex items-center mt-2 rounded-md border border-input bg-card focus-within:ring-2 focus-within:ring-ring overflow-hidden">
                <span className="px-3 py-2.5 text-sm text-muted-foreground border-r border-input font-mono">
                  +91
                </span>
                <input
                  data-testid="login-phone-input"
                  autoFocus
                  inputMode="numeric"
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendOtp()}
                  className="flex-1 bg-transparent px-3 py-2.5 outline-none text-base tracking-wide"
                />
              </div>

              <Button
                data-testid="login-send-otp-btn"
                onClick={sendOtp}
                disabled={loading}
                className="w-full mt-6 h-11 text-base font-semibold group"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Send OTP
                    <ArrowRight className="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="otp"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-3">
                <ShieldCheck className="h-3.5 w-3.5" /> Verify
              </p>
              <h1 className="font-display font-black text-4xl leading-tight mb-3">
                Enter the code
              </h1>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                We sent a 6-digit code to{" "}
                <span className="text-foreground font-medium">+91 {cleanPhone}</span>.
              </p>

              <div ref={otpRef}>
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={(v) => {
                    setCode(v);
                    if (v.length === 6) verify(v);
                  }}
                  data-testid="login-otp-input"
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="h-12 w-11 text-lg font-mono rounded-md border-input"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                data-testid="login-verify-btn"
                onClick={() => verify()}
                disabled={loading || code.length !== 6}
                className="w-full mt-8 h-11 text-base font-semibold"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Sign in"}
              </Button>

              <div className="flex items-center justify-between mt-5 text-sm">
                <button
                  data-testid="login-change-phone-btn"
                  onClick={() => {
                    setStep("phone");
                    setCode("");
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Change number
                </button>
                <button
                  data-testid="login-resend-btn"
                  onClick={sendOtp}
                  className="text-primary font-medium hover:underline"
                >
                  Resend code
                </button>
              </div>
              <p className="mt-6 text-xs text-muted-foreground bg-muted rounded-md px-3 py-2">
                Demo environment — use OTP <span className="font-mono font-semibold text-foreground">123456</span>
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={BRAND.loginBg} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <img src={BRAND.square} alt="" className="h-16 w-16 rounded-2xl mb-8 shadow-xl" />
          <h2 className="font-display font-black text-4xl leading-tight max-w-md">
            Every order, packed &amp; paid — on one screen.
          </h2>
          <p className="mt-4 text-white/80 max-w-sm leading-relaxed">
            Accept orders, set prep times, and print thermal bills for your counter, all in real time.
          </p>
          <a href="https://www.1apponly.com" target="_blank" rel="noreferrer" className="mt-8 text-sm font-semibold text-white/90 hover:text-white transition-colors" data-testid="login-website">
            {WEBSITE}
          </a>
        </div>
      </div>
    </div>
  );
}
